import { BECategory } from 'constants/categories';
import type { RestrictedTradingCategory } from 'constants/featureFlags';
import { selectPerpsOnlyStatus, type PerpsOnlyStatus } from 'lib/perpsOnlyStatus';
import { useAuthStore } from 'lib/stores/authStore';
import { useFeatureFlagStore } from 'lib/stores/featureFlagStore';
import { isSportsMilestoneType, type MilestoneType } from 'models/milestones';
import config from 'utils/config';

import {
    GEOCOMPLY_LOCATION_ACCESS_BLOCKED_HELP,
    GEOCOMPLY_LOCATION_ACCESS_BLOCKED_TITLE,
    GEOCOMPLY_RETRY_OR_SUPPORT_HELP,
    GEOCOMPLY_VERIFICATION_INCOMPLETE_TITLE,
    getGeoComplyClientErrorPolicy,
    type GeoComplyClientErrorNoticeKind,
    type GeoComplyClientErrorPolicy,
} from './browserGuardErrors';
import { accountRequiresGeoComply } from './geoComplyEligibility';
import {
    isGeoComplyVerificationCurrent,
    useGeoComplyStore,
    type GeoComplyDenyReason,
    type GeoComplyStatus,
    type GeoComplyVerificationRequiredReason,
} from './geoComplyStore';

export const SPORTS_LOCATION_VERIFICATION_MESSAGE = GEOCOMPLY_LOCATION_ACCESS_BLOCKED_TITLE;
export const SPORTS_LOCATION_PERMISSION_HELP = 'Allow location access in your browser, then try again.';
/** Placeholder for a protected action no verdict has cleared yet. */
export const SPORTS_LOCATION_CHECKING_LABEL = 'Checking location…';
/** Shown after BrowserGuard reports 611: the check ran but the browser had no coordinates to give it. */
export const SPORTS_LOCATION_ACCESS_BLOCKED_HELP = GEOCOMPLY_LOCATION_ACCESS_BLOCKED_HELP;
/**
 * Recovery copy for a software block, mirroring mobile v2's takeover. The
 * headless BrowserGuard we load never shows GeoComply's own UX hint, so
 * without this the user is told they're blocked and nothing else — these are
 * the steps that actually clear it.
 */
export const SPORTS_LOCATION_SOFTWARE_BLOCK_TITLE = 'Location check blocked';
export const SPORTS_LOCATION_SOFTWARE_BLOCK_DESCRIPTION = 'An app on your device is in the way, not your location.';
export const SPORTS_LOCATION_SOFTWARE_BLOCK_STEPS = [
    "Turn off VPN if you're using one",
    'Close any location-changing apps',
    'Come back and select Try again',
] as const;
export const SPORTS_LOCATION_SOFTWARE_BLOCK_RETRY = 'Try again';

export const SPORTS_LOCATION_SERVICE_UNAVAILABLE_TITLE = "Location check couldn't load";
export const SPORTS_LOCATION_SERVICE_UNAVAILABLE_HELP =
    'Turn off ad blockers or privacy extensions for this site, then refresh the page.';
export const GEOCOMPLY_DEVICE_TIME_INCORRECT_TITLE = "Check your device's date and time";
export const GEOCOMPLY_DEVICE_TIME_INCORRECT_HELP =
    'Set your date, time, and time zone to update automatically, then try verifying your location again.';

export interface GeoComplyRecoveryMessage {
    title: string;
    description: string;
}

function getClientErrorPolicy(
    reason: GeoComplyVerificationRequiredReason | null,
    errorCode: number | null,
): GeoComplyClientErrorPolicy | undefined {
    if (reason !== 'authorization_failed' && reason !== 'no_coordinates' && reason !== 'sdk_no_result') {
        return undefined;
    }
    return getGeoComplyClientErrorPolicy(errorCode);
}

/** User-facing recovery copy for the reason the latest verification could not unblock trading. */
export function getGeoComplyVerificationRequiredMessage(
    reason: GeoComplyVerificationRequiredReason | null,
    errorCode: number | null,
): GeoComplyRecoveryMessage {
    const clientErrorPolicy = getClientErrorPolicy(reason, errorCode);
    if (clientErrorPolicy) {
        return { title: clientErrorPolicy.title, description: clientErrorPolicy.description };
    }

    switch (reason) {
        case 'denied_masking_service':
            return {
                title: 'Turn off location-masking tools',
                description: 'Turn off any VPN, proxy, or iCloud Private Relay, then try again.',
            };
        case 'denied_boundary_exclusion':
        case 'denied_unconfirmed_boundary':
            return {
                title: "We couldn't confirm your location",
                description:
                    'Turn on precise location and move away from a state or jurisdiction border, then try again.',
            };
        case 'no_coordinates':
        case 'denied_blocked_software_without_location_permission':
        case 'denied_out_of_boundary_without_location_permission':
        case 'denied_unknown_without_location_permission':
            return {
                title: SPORTS_LOCATION_VERIFICATION_MESSAGE,
                description: SPORTS_LOCATION_ACCESS_BLOCKED_HELP,
            };
        case 'denied_approximate_location':
        case 'denied_not_tradeable_state_without_region':
        case 'denied_requires_precise_location':
        case 'denied_unknown_requires_precise_location':
            return {
                title: 'Precise location required',
                description: 'Turn on precise location for this site, then try again.',
            };
        case 'device_time_incorrect':
            return {
                title: GEOCOMPLY_DEVICE_TIME_INCORRECT_TITLE,
                description: GEOCOMPLY_DEVICE_TIME_INCORRECT_HELP,
            };
        // The malformed-location cases in this operational group describe an
        // authenticated backend response with an unusable protocol shape, not
        // a browser permission problem. Precise-location instructions cannot
        // repair them, so keep them on retry/support copy.
        case 'authorization_failed':
        case 'backend_error_code_missing':
        case 'backend_request_failed':
        case 'backend_response_invalid':
        case 'backend_user_mismatch':
        case 'check_failed':
        case 'check_timed_out':
        case 'location_country_missing':
        case 'location_invalid_type':
        case 'location_missing':
        case 'location_us_region_missing':
        case 'sdk_no_result':
        case 'trust_window_elapsed_before_apply':
            return {
                title: GEOCOMPLY_VERIFICATION_INCOMPLETE_TITLE,
                description: GEOCOMPLY_RETRY_OR_SUPPORT_HELP,
            };
        default:
            return {
                title: SPORTS_LOCATION_VERIFICATION_MESSAGE,
                description: SPORTS_LOCATION_PERMISSION_HELP,
            };
    }
}

/** Whether this failure has a user-actionable path to another SDK transaction. */
export function canRetryGeoComplyVerification(
    reason: GeoComplyVerificationRequiredReason | null,
    errorCode: number | null,
): boolean {
    return getClientErrorPolicy(reason, errorCode)?.canRetry ?? true;
}

/** A 602 retry cannot start until the browser regains network connectivity. */
export function shouldWaitForGeoComplyConnection(
    reason: GeoComplyVerificationRequiredReason | null,
    errorCode: number | null,
    isOnline: boolean,
): boolean {
    return !isOnline && getClientErrorPolicy(reason, errorCode)?.recovery === 'retry' && errorCode === 602;
}

/**
 * Whether location enforcement runs in this environment at all. Both the
 * boundary UI and the request guard read it, so they cannot disagree, and
 * `config.geoComplyEnabled` is the single deploy-time switch behind it.
 */
export function isGeoComplyEnforced(): boolean {
    return config.geoComplyEnabled;
}

/**
 * Built-in fallback for the geo-enforced restricted-trading entries,
 * mirroring `restricted_trading_categories` in product-metadata
 * `app_casual/feature_flags.json5`. The feature-flag store's fetched
 * entries override this whenever any geo-flagged entry is present.
 */
const DEFAULT_GEO_RESTRICTED_ENTRIES: readonly RestrictedTradingCategory[] = [
    { state: 'NV', categories: ['Sports', 'Elections', 'Entertainment'], subcategories: ['Sports'], geo: true },
    { state: 'MI', categories: ['Sports'], subcategories: ['Sports'], geo: true },
    {
        state: 'WA',
        categories: ['Sports', 'Elections', 'Entertainment', 'Politics', 'Science and Technology', 'Mentions'],
        subcategories: ['Sports'],
        geo: true,
        sell_positions_allowed: true,
    },
    { state: 'TRIBE_BL', categories: ['Sports'], subcategories: ['Sports'], geo: true },
    { state: 'TRIBE_BV', categories: ['Sports'], subcategories: ['Sports'], geo: true },
    { state: 'TRIBE_CRR', categories: ['Sports'], subcategories: ['Sports'], geo: true },
];

function getGeoRestrictedEntries(): readonly RestrictedTradingCategory[] {
    const fetched = (useFeatureFlagStore.getState().restrictedTradingCategories ?? []).filter(
        (entry) => entry.geo === true,
    );
    return fetched.length > 0 ? fetched : DEFAULT_GEO_RESTRICTED_ENTRIES;
}

function normalizedRestrictionValues(values: readonly string[]): Set<string> {
    return new Set(values.map((value) => value.trim().toLowerCase()).filter(Boolean));
}

export interface SportsTradingMarketData {
    category?: string | null;
    /**
     * Series tags, when the caller has them. The restriction config's
     * `subcategories` (e.g. NV's `['Sports']`) exist to catch series tagged
     * into a restricted vertical outside its category — the mobile apps match
     * tags the same way.
     */
    tags?: readonly string[] | null;
    milestoneType?: MilestoneType;
}

declare const sportsTradingScopeBrand: unique symbol;

export interface SportsTradingScope {
    /** Lowercased category values of the trade's markets (sports milestones report 'sports'). */
    readonly categoryValues: readonly string[];
    /** Lowercased series tags, matched only against restriction `subcategories`. */
    readonly seriesTagValues: readonly string[];
    /** True when any market lacked classification data; such trades fail closed. */
    readonly containsUnknownMarkets: boolean;
    /**
     * True for a position-capped sell (`order_action: 'sell'` with
     * `sell_position_capped`), which can only shrink a position the user
     * already holds. Restriction entries flagged `sell_positions_allowed` let
     * such trades through where they refuse a buy.
     */
    readonly sellsPosition: boolean;
    readonly requiresLocationVerification: boolean;
    readonly [sportsTradingScopeBrand]: true;
}

/**
 * Thrown by the trade guard so callers can tell a client-side location block
 * apart from a server error without matching on message copy.
 */
export class SportsTradingLocationError extends Error {
    constructor(
        message: string,
        readonly reason: 'blocked' | 'verification-required',
    ) {
        super(message);
        this.name = 'SportsTradingLocationError';
    }
}

type SportsTradingScopeClassification = Pick<
    SportsTradingScope,
    'categoryValues' | 'seriesTagValues' | 'containsUnknownMarkets' | 'sellsPosition'
>;

/**
 * Whether the trade's categories are geo-restricted anywhere per the current
 * configuration. The remote config loads asynchronously, so enforcement
 * points must call this at decision time rather than trusting the snapshot
 * a derived scope captured.
 */
export function scopeRequiresLocationVerification(scope: SportsTradingScopeClassification): boolean {
    return getGeoRestrictedEntries().some((entry) => entryRestrictsScope(entry, scope));
}

/**
 * Whether this entry's state refuses the trade. A position sell passes
 * wherever the entry allows selling positions, whatever the market's
 * classification: it can only shrink a held position, and a category the
 * entry doesn't list was never restricted there. Otherwise categories and
 * tags are matched, with unclassified markets failing closed.
 */
function entryRestrictsScope(entry: RestrictedTradingCategory, scope: SportsTradingScopeClassification): boolean {
    if (scope.sellsPosition && entry.sell_positions_allowed) return false;
    if (scope.containsUnknownMarkets) return true;
    const restrictedCategories = normalizedRestrictionValues(entry.categories);
    const restrictedSubcategories = normalizedRestrictionValues(entry.subcategories ?? []);
    return (
        scope.categoryValues.some((value) => restrictedCategories.has(value)) ||
        scope.seriesTagValues.some((value) => restrictedSubcategories.has(value))
    );
}

export function deriveSportsTradingScope(
    markets: readonly SportsTradingMarketData[],
    { sellsPosition = false }: { sellsPosition?: boolean } = {},
): SportsTradingScope {
    const categoryValues = new Set<string>();
    const seriesTagValues = new Set<string>();
    let containsUnknownMarkets = markets.length === 0;

    for (const market of markets) {
        const category = market.category?.trim().toLowerCase();
        if (category) categoryValues.add(category);
        for (const tag of market.tags ?? []) {
            const normalized = tag.trim().toLowerCase();
            if (normalized) seriesTagValues.add(normalized);
        }
        if (isSportsMilestoneType(market.milestoneType)) {
            categoryValues.add(BECategory.SPORTS.toLowerCase());
        } else if (!category) {
            containsUnknownMarkets = true;
        }
    }

    const partialScope = {
        categoryValues: [...categoryValues],
        seriesTagValues: [...seriesTagValues],
        containsUnknownMarkets,
        sellsPosition,
    };
    const scope: Omit<SportsTradingScope, typeof sportsTradingScopeBrand> = {
        ...partialScope,
        requiresLocationVerification: scopeRequiresLocationVerification(partialScope),
    };
    return scope as SportsTradingScope;
}

/**
 * Whether the confirmed region blocks this trade: true when any of the
 * trade's categories is geo-restricted in that region, unless the trade only
 * sells a position and the region allows that. A missing scope fails closed
 * in restricted regions, as does one containing unclassified markets.
 */
function isScopeBlockedInRegion(scope: SportsTradingScope | undefined, usRegionCode: string | null): boolean {
    if (!usRegionCode) return false;
    const entry = getGeoRestrictedEntries().find((candidate) => candidate.state.trim().toUpperCase() === usRegionCode);
    if (!entry) return false;
    if (!scope) return true;
    return entryRestrictsScope(entry, scope);
}

/**
 * Which kind of problem the notice is reporting, so it can carry an icon that
 * matches: where the user is, or what is running on their machine.
 */
export type GeoComplyNoticeKind = GeoComplyClientErrorNoticeKind;

/** Visual category for a recoverable verification failure. */
export function getGeoComplyVerificationRequiredKind(
    reason: GeoComplyVerificationRequiredReason | null,
    errorCode: number | null = null,
): GeoComplyNoticeKind | undefined {
    const clientErrorKind = getClientErrorPolicy(reason, errorCode)?.noticeKind;
    if (clientErrorKind) return clientErrorKind;

    switch (reason) {
        case 'denied_masking_service':
        case 'denied_blocked_software_without_location_permission':
            return 'software';
        case 'denied_approximate_location':
        case 'denied_boundary_exclusion':
        case 'denied_not_tradeable_state_without_region':
        case 'denied_out_of_boundary_without_location_permission':
        case 'denied_requires_precise_location':
        case 'denied_unconfirmed_boundary':
        case 'denied_unknown_requires_precise_location':
        case 'denied_unknown_without_location_permission':
        case 'no_coordinates':
            return 'location';
        case 'device_time_incorrect':
            return 'waiting';
        default:
            return undefined;
    }
}

export interface GeoComplyDenyMessage {
    title: string;
    description: string;
    kind: GeoComplyNoticeKind;
    action?: {
        href: string;
        label: string;
    };
}

const UNKNOWN_DENY_MESSAGE: GeoComplyDenyMessage = {
    title: 'Trading unavailable in your area',
    description: "To comply with local laws, trading is currently unavailable. You'll still be able to browse markets.",
    kind: 'location',
};

/**
 * Deny copy per reason, mirroring the mobile apps' built-in
 * `geocomply_deny_messages`. Reasons without an entry use the unknown copy.
 */
const DENY_MESSAGES: Partial<Record<GeoComplyDenyReason, GeoComplyDenyMessage>> = {
    failed_with_precise_location: {
        title: 'Trading is currently unavailable',
        description: 'To comply with local laws, geo-spoofing software is not allowed.',
        kind: 'software',
    },
    blocked_software: {
        title: 'Trading is currently unavailable',
        description: 'To comply with local laws, altered or tampered software is not allowed.',
        kind: 'software',
    },
    not_tradeable_state: {
        title: 'Some markets are unavailable in your state',
        description:
            'Your state government is preventing your access to fair, federally-regulated prediction market trading with no house advantage. Act Now. Contact your lawmaker and join Freedom to Forecast.',
        kind: 'location',
        action: {
            href: 'https://www.freedomtoforecast.com/',
            label: 'Act Now',
        },
    },
    unknown: UNKNOWN_DENY_MESSAGE,
};

/**
 * The deny copy for a blocked state. A confirmed state is required before we
 * show the state-government message; otherwise the unknown-location copy is
 * used.
 */
export function getGeoComplyDenyMessage(
    denyReason: GeoComplyDenyReason,
    usRegionCode: string | null,
): GeoComplyDenyMessage {
    const entry = DENY_MESSAGES[denyReason] ?? UNKNOWN_DENY_MESSAGE;
    if (denyReason !== 'not_tradeable_state') return entry;

    const regionEntry = usRegionCode
        ? getGeoRestrictedEntries().find((candidate) => candidate.state.trim().toUpperCase() === usRegionCode)
        : undefined;
    if (!regionEntry || regionEntry.categories.length === 0) return UNKNOWN_DENY_MESSAGE;
    return entry;
}

export type GeoComplyAccessDecision =
    | { outcome: 'allowed' }
    | ({ outcome: 'blocked' } & GeoComplyDenyMessage)
    | { outcome: 'verification-required' };

/**
 * The single access decision shared by the boundary UI and the trade
 * request guard, so both always agree. A verified (or verdict-confirmed)
 * region blocks only the categories that region restricts, and lets a
 * position sell through where its entry allows one; a confirmed region that
 * doesn't block the trade counts as a pass, like the mobile apps.
 */
export function resolveGeoComplyAccess(
    scope: SportsTradingScope | undefined,
    state: {
        status: GeoComplyStatus;
        validUntil: number | null;
        usRegionCode: string | null;
        denyReason: GeoComplyDenyReason | null;
    },
): GeoComplyAccessDecision {
    if (isGeoComplyVerificationCurrent(state)) {
        if (!isScopeBlockedInRegion(scope, state.usRegionCode)) return { outcome: 'allowed' };
        return { outcome: 'blocked', ...getGeoComplyDenyMessage('not_tradeable_state', state.usRegionCode) };
    }

    if (state.status === 'blocked') {
        const denyReason = state.denyReason ?? 'unknown';
        if (denyReason === 'not_tradeable_state') {
            if (!state.usRegionCode) return { outcome: 'verification-required' };
            if (!isScopeBlockedInRegion(scope, state.usRegionCode)) return { outcome: 'allowed' };
        }
        return { outcome: 'blocked', ...getGeoComplyDenyMessage(denyReason, state.usRegionCode) };
    }

    return { outcome: 'verification-required' };
}

export function assertSportsTradingAllowed(scope: SportsTradingScope | undefined): void {
    if (!accountRequiresGeoComply(selectPerpsOnlyStatus(useAuthStore.getState()))) return;
    // Re-evaluated against the current config: a scope derived before the
    // remote restriction entries loaded must not bake in a stale pass.
    if (scope && !scopeRequiresLocationVerification(scope)) return;
    if (!isGeoComplyEnforced()) return;

    const decision = resolveGeoComplyAccess(scope, useGeoComplyStore.getState());
    if (decision.outcome === 'allowed') return;
    if (decision.outcome === 'blocked') {
        throw new SportsTradingLocationError(decision.description, 'blocked');
    }
    throw new SportsTradingLocationError(SPORTS_LOCATION_VERIFICATION_MESSAGE, 'verification-required');
}

function resolveSessionClearsAllSportsTradingScopes(
    perpsOnlyStatus: PerpsOnlyStatus,
    state: {
        status: GeoComplyStatus;
        validUntil: number | null;
        usRegionCode: string | null;
        denyReason: GeoComplyDenyReason | null;
    },
): boolean {
    if (!accountRequiresGeoComply(perpsOnlyStatus)) return true;
    if (!isGeoComplyEnforced()) return true;
    return resolveGeoComplyAccess(undefined, state).outcome === 'allowed';
}

/**
 * True when this session's GeoComply state alone guarantees no trade can be
 * geo-blocked, regardless of market classification: enforcement is off for
 * this deploy, or a current verification places the user somewhere without
 * geo-restriction entries — the same undefined-scope decision
 * `assertSportsTradingAllowed` would reach, so UI gated on this can enable
 * an action before its markets' classifications have resolved without ever
 * disagreeing with the submit-time guard.
 *
 * This is an imperative snapshot for non-React callers. React render paths
 * must use `useSessionClearsAllSportsTradingScopes` so auth and GeoComply
 * transitions cannot be hidden from React Compiler.
 */
export function sessionClearsAllSportsTradingScopes(): boolean {
    return resolveSessionClearsAllSportsTradingScopes(
        selectPerpsOnlyStatus(useAuthStore.getState()),
        useGeoComplyStore.getState(),
    );
}

/** Reactive form for render consumers; request-time guards should keep using fresh imperative reads. */
export function useSessionClearsAllSportsTradingScopes(): boolean {
    const perpsOnlyStatus = useAuthStore(selectPerpsOnlyStatus);
    const status = useGeoComplyStore((state) => state.status);
    const validUntil = useGeoComplyStore((state) => state.validUntil);
    const usRegionCode = useGeoComplyStore((state) => state.usRegionCode);
    const denyReason = useGeoComplyStore((state) => state.denyReason);

    return resolveSessionClearsAllSportsTradingScopes(perpsOnlyStatus, {
        status,
        validUntil,
        usRegionCode,
        denyReason,
    });
}
